import { AlertCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

/**
 * PremiumBanner — extraído de Dashboard.jsx en P3.1 sesión 2/4 (feb-2026).
 *
 * Banner Premium con botones de gestionar / cancelar / reactivar suscripción
 * + estado de cancelación programada. JSX y clases Tailwind IDÉNTICOS al
 * original.
 *
 * El modal de confirmación de cancelación está en CancelSubscriptionModal.jsx
 * para no anidar 2 modales en este archivo.
 *
 * Reglas del cuadro negro:
 * - NO modifica handlers, solo los recibe como props.
 * - NO toca billing (Stripe portal), solo dispara los handlers.
 */
const PremiumBanner = ({
  // visibilidad
  isPremium,
  showSubscriptionWidget,
  latestWorkspaceImage,
  showImageEditor,
  forceInternalWorkspace,
  // datos de suscripción
  data,
  canCancelSubscription,
  subscriptionScheduledToCancel,
  // estado de loading
  isSubActionLoading,
  // handlers
  onManageSubscription,
  onReactivate,
  onRequestCancel,
}) => {
  const { language } = useLanguage();

  // Mostrar solo si Premium + widget activo + sin imagen ni workspace forzado.
  if (!isPremium || !showSubscriptionWidget) return null;
  if (latestWorkspaceImage) return null;
  if (showImageEditor) return null;
  if (forceInternalWorkspace) return null;

  return (
    <div className="flex-shrink-0 px-4 py-3" data-testid="premium-banner">
      <div className="flex flex-col gap-3 bg-gradient-to-r from-slate-800 to-purple-900 text-white p-3 sm:p-5 rounded-xl mb-0 shadow-lg border border-slate-700">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-sm font-semibold">
              Premium
            </span>

            <div>
              <h2 className="text-base sm:text-lg font-semibold">
                {language === 'es'
                  ? 'Gracias por apoyar DamyxIA 💜'
                  : 'Thanks for supporting DamyxIA 💜'}
              </h2>
              <p className="text-slate-300 text-sm">
                {language === 'es'
                  ? 'Tienes acceso completo a todas las capacidades de la plataforma.'
                  : 'You have full access to all platform capabilities.'}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={onManageSubscription}
              className="px-3 sm:px-4 py-2 rounded-full bg-slate-900/80 border border-slate-600 text-white text-xs sm:text-sm font-medium hover:bg-slate-900 transition whitespace-nowrap"
              data-testid="premium-banner-manage-btn"
            >
              {language === 'es' ? 'Gestionar suscripción' : 'Manage subscription'}
            </button>

            {/* Cancelar / Reactivar — solo si la suscripcion tiene sub_id real */}
            {canCancelSubscription &&
              (subscriptionScheduledToCancel ? (
                <button
                  onClick={onReactivate}
                  disabled={isSubActionLoading !== null}
                  className="px-3 sm:px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-xs sm:text-sm font-medium transition whitespace-nowrap disabled:opacity-60 flex items-center gap-1.5"
                  data-testid="premium-banner-reactivate-btn"
                >
                  {isSubActionLoading === 'reactivate' && (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  )}
                  {language === 'es' ? 'Reactivar suscripción' : 'Reactivate subscription'}
                </button>
              ) : (
                <button
                  onClick={onRequestCancel}
                  disabled={isSubActionLoading !== null}
                  className="px-3 sm:px-4 py-2 rounded-full bg-transparent border border-red-400/50 text-red-300 hover:text-red-200 hover:bg-red-500/10 text-xs sm:text-sm font-medium transition whitespace-nowrap"
                  data-testid="premium-banner-cancel-btn"
                >
                  {language === 'es' ? 'Cancelar suscripción' : 'Cancel subscription'}
                </button>
              ))}
          </div>
        </div>

        {/* Banner Cancelacion programada */}
        {subscriptionScheduledToCancel && (
          <div
            className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg"
            data-testid="premium-banner-cancel-scheduled"
          >
            <AlertCircle className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
            <div className="text-xs sm:text-sm text-amber-200">
              <span className="font-semibold">
                {language === 'es' ? 'Cancelación programada.' : 'Cancellation scheduled.'}
              </span>{' '}
              <span className="text-amber-300/90">
                {language === 'es'
                  ? 'Tu suscripción se cancelará el '
                  : 'Your subscription will end on '}
                {data?.cancel_at
                  ? new Date(data.cancel_at).toLocaleDateString(
                      language === 'es' ? 'es-ES' : 'en-US',
                      { day: '2-digit', month: 'long', year: 'numeric' },
                    )
                  : '—'}
                .
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PremiumBanner;
